import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { type ReactNode } from 'react';

import { AuthProvider } from '../hooks/useAuth';
import { ApiError } from '../lib/api';
import { PrefsProvider, usePrefs } from '../features/analytics/shell/prefs';
import { TooltipProvider } from '../ui';

/**
 * One QueryClient for the app.
 *
 * `retry` is deliberate rather than default: a 4xx is an answer, not a blip, and
 * retrying it three times only delays the error the user needs to see. A network
 * failure or a 5xx is worth one more attempt.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false;
  if (error instanceof ApiError) {
    if (error.status === 0) return true; // network / timeout
    return error.status >= 500;
  }
  return false;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache(),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: shouldRetry,
      },
      mutations: {
        retry: false,
      },
    },
  });
}

/** Sonner, themed to match the app rather than guessing from the OS. */
function AppToaster() {
  const { resolvedTheme } = usePrefs();
  return (
    <Toaster
      theme={resolvedTheme}
      position="bottom-right"
      closeButton
      // The design system already owns colour; let Sonner inherit it.
      toastOptions={{
        classNames: {
          toast:
            'border border-border bg-surface-raised text-fg shadow-overlay rounded-lg text-[0.8125rem]',
          description: 'text-fg-muted',
          actionButton: 'bg-primary text-primary-fg rounded-md',
          cancelButton: 'bg-surface-hover text-fg-secondary rounded-md',
        },
      }}
    />
  );
}

export function AppProviders({
  children,
  queryClient,
}: {
  children: ReactNode;
  queryClient: QueryClient;
}) {
  return (
    <QueryClientProvider client={queryClient}>
      <PrefsProvider>
        <AuthProvider>
          <TooltipProvider>
            {children}
            <AppToaster />
          </TooltipProvider>
        </AuthProvider>
      </PrefsProvider>
    </QueryClientProvider>
  );
}
